import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';

const STORAGE_KEY = 'sidebar-collapsed';
const BODY_CLASS = 'sidebar-collapsed';

const readInitialCollapsed = () => {
  // Collapsed by default; an explicit user toggle (persisted either way)
  // takes precedence.
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === '1';
  } catch (_e) {
    return true;
  }
};

// Apply the body class synchronously at module import so the layout CSS
// variables resolve correctly on the very first paint after a refresh.
// Without this, the sidebar renders narrow but the header/content gutter
// briefly stays at the expanded width until the React effect fires.
if (typeof document !== 'undefined' && document.body) {
  if (readInitialCollapsed()) {
    document.body.classList.add(BODY_CLASS);
  }
}

const SidebarContext = createContext({
  collapsed: false,
  toggle: () => {},
  setCollapsed: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

export const SidebarProvider = ({ children }) => {
  const [userCollapsed, setUserCollapsedState] = useState(readInitialCollapsed);
  // Phones/tablets always get the icon rail. The stored preference is
  // NOT touched, so a desktop session keeps whatever the user chose.
  const isMobile = useMediaQuery('(max-width:899.95px)', { noSsr: true });
  const collapsed = isMobile ? true : userCollapsed;

  useLayoutEffect(() => {
    if (collapsed) {
      document.body.classList.add(BODY_CLASS);
    } else {
      document.body.classList.remove(BODY_CLASS);
    }
  }, [collapsed]);

  const persist = (v) => {
    try {
      localStorage.setItem(STORAGE_KEY, v ? '1' : '0');
    } catch (_e) {
      // ignore quota / private mode errors
    }
  };

  const setCollapsed = useCallback((next) => {
    setUserCollapsedState((prev) => {
      const v = typeof next === 'function' ? next(prev) : !!next;
      persist(v);
      return v;
    });
  }, []);

  const toggle = useCallback(() => {
    setUserCollapsedState((prev) => {
      persist(!prev);
      return !prev;
    });
  }, []);

  const value = useMemo(
    () => ({ collapsed, isMobile, toggle, setCollapsed }),
    [collapsed, isMobile, toggle, setCollapsed]
  );

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

export default SidebarProvider;
